<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-02-28 (0.1.0) (jhoward)
 *   - Add ViewOffer.vue for displaying offer details, prizes, and booking/redeem actions.
 *
 * 2026-03-02 (0.1.1) (jhoward)
 *   - Refactor offer detail view to use PeppermillContentBlock and PeppermillActions.
 *   - Add show/hide disclaimer toggle and improved action links.
 *
 * 2026-03-11 (0.2.0) (jhoward)
 *   - Refactor to use getAccountOffer helper and OfferManagementDialog component.
 *   - Replace router and fetch logic with new dialog-based redemption/void flow.
 *   - Remove DisplayPrizes and selectedPrizeId logic.
 *   - Update actions for redeem/cancel and disclaimer toggling.
 *
 * 2026-03-13 (0.3.0) (jhoward)
 *   - Replace RedeemOfferDialog with OfferManagementDialog.
 *   - Update dialog logic to use appStore.offerManagementDialog.
 *
 * 2026-03-13 (0.3.1) (jhoward)
 *   - Use appStore.getOfferImageUrl to get offer image URL.
 *   - Update template to use computed offerImageUrl for image display.
 *   - Add layout styling for content block.
 *
 * 2026-03-13 (0.3.2) (jhoward)
 *   - Show check_circle icon for accepted offers.
 *   - Reduce max-width of content block from 800px to 600px.
 *
 * 2026-03-17 (0.4.0) (jhoward)
 *   - Add 'BOOK ROOM' button with validateHotelOffer logic.
 *   - Add isOfferRedeemed and isOfferSoldOut UI logic.
 *   - Add back button to all offers.
 *   - Refactor offer loading and action logic.
 *
 * 2026-03-24 (0.5.0) (jhoward)
 *   - Update logic for BOOK ROOM and REDEEM buttons to be mutually exclusive based on offer properties.
 *
 * 2026-03-30 (0.5.1) (jhoward)
 *   - Import validateHotelOffer from helper and remove inline implementation.
 *
 * 2026-04-09 (0.6.0) (jhoward)
 *   - Add import and usage of src\helpers\isOfferForLinkedAccount for linked account detection.
 *   - Disable BOOK ROOM action for linked account offers and show warning via appStore.addSnack.
 *   - Add visual indicator (material-icons) for linked account offers.
 *   - Update CSS for new linked account indicator.
 *
 * 2026-09-08 (0.7.0) (jhoward)
 *   - Replace the floating check_circle icon with an App\components\offers\OfferRedeemedStatus.vue banner above the
 *     offer title. This is the page a guest opens to check whether they already claimed something, so the answer goes
 *     first, in words, naming who redeemed it and which prize was taken.
 *   - Remove the now unused .offer--accepted rules and the stacking hack they needed.
 *
 * 2026-09-09 (0.8.0) (jhoward)
 *   - Show how long the offer has left under the title via App\components\offers\OfferExpiryStatus.vue.
 *   - Offer an .ics download and a Google Calendar link for offers with a real date, via
 *     App\components\offers\OfferCalendarLinks.vue.
 *
 * 2026-09-09 (0.9.0) (jhoward)
 *   - Move App\components\offers\OfferCalendarLinks.vue out of <peppermill-actions> and in beside the expiry status,
 *     which is now a .offer--status_row rather than a paragraph. The component renders a pill instead of two links,
 *     and .layout--actions floats and uppercases anchors only, so a pill left in there would have sat out of flow
 *     ahead of the CTAs.
 *
 * 2026-09-23 (0.10.0) (jhoward)
 *   - Add the App\components\offers\OfferPendingStatus.vue banner above the offer, so an admin viewing a PENDING
 *     offer is told it is not live for patrons yet.
 */

import PeppermillActions from '@framework/components/layout/PeppermillActions.vue';
import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import { onMounted, ref, watch } from 'vue';
import PeppermillViewport from '@/components/framework-proxy/PeppermillViewport.vue';
import OfferCalendarLinks from '@/components/offers/OfferCalendarLinks.vue';
import OfferExpiryStatus from '@/components/offers/OfferExpiryStatus.vue';
import OfferPendingStatus from '@/components/offers/OfferPendingStatus.vue';
import OfferRedeemedStatus from '@/components/offers/OfferRedeemedStatus.vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import getAccountOffer from '@/helpers/getAccountOffer';
import isOfferForLinkedAccount from '@/helpers/isOfferForLinkedAccount';
import isOfferSoldOut from '@/helpers/isOfferSoldOut';
import validateHotelOffer from '@/helpers/validateHotelOffer';
import useAppStore from '@/store';

const props = defineProps({
  offerDomain: {
    type: String,
    default: '',
  },
  offerCode: {
    type: String,
    default: '',
  },
});

const appStore = useAppStore();
const offer = ref<OpcOffer | null>(null);
// const bookUrl = ref<string | null>(null);
const isDisclaimerVisible = ref<boolean>(false);
const offerImageUrl = ref<string | null>(null);
// let loadHotelOfferTimer = -1;

// function handleBookRoomClick() {
//   appStore.isOverlayActive = true;
//   appStore.isLoadingSpinnerActive = true;
//   appStore.overlayText = 'Redirecting to booking engine...';
//   window.location.href = bookUrl.value || '#';
// }

async function loadOffer() {
  offer.value = await getAccountOffer(props.offerDomain, props.offerCode);
  if (offer.value) {
    offerImageUrl.value = appStore.getOfferImageUrl(offer.value, '800');

    // clearTimeout(loadHotelOfferTimer);
    // loadHotelOfferTimer = setTimeout(async () => {
    //   if (offer.value?.RESNET_OFFER_CODE.length) {
    //     bookUrl.value = await getHotelOfferValidation(offer.value);
    // }
    // }, 132);
  }
}

watch(appStore.account, loadOffer);
watch(appStore.offerManagementDialog, loadOffer);
onMounted(loadOffer);
</script>

<template>
  <peppermill-viewport>
    <div class="view_offer--back_btn_wrapper">
      <router-link to="/offers" class="view_offer--back_btn"
        ><span class="material-icons">arrow_back</span>Back to All Offers</router-link
      >
    </div>
    <peppermill-content-block>
      <offer-pending-status v-if="offer" :offer="offer" is-banner></offer-pending-status>
      <offer-redeemed-status v-if="offer" :offer="offer" is-banner></offer-redeemed-status>

      <img class="offer--image" v-if="offer && offerImageUrl" :src="offerImageUrl" :alt="offer.OFFER_COPY_TITLE" />

      <h2 v-if="offer?.OFFER_COPY_TITLE">{{ offer.OFFER_COPY_TITLE }}</h2>
      <h3 v-if="offer?.OFFER_COPY_SUBTITLE">{{ offer.OFFER_COPY_SUBTITLE }}</h3>

      <!--The calendar control sits with the expiry rather than in <peppermill-actions>: that block floats and
          uppercases its anchors and floats nothing else, so a pill dropped in there would sit out of flow ahead of
          the CTAs.-->
      <div v-if="offer" class="offer--status_row">
        <offer-expiry-status :offer="offer"></offer-expiry-status>
        <offer-calendar-links :offer="offer"></offer-calendar-links>
      </div>

      <div class="offer--body" v-if="offer?.OFFER_COPY_BODY.length" v-html="offer.OFFER_COPY_BODY"></div>

      <div
        class="offer--disclaimer"
        v-if="offer?.OFFER_COPY_DISCLAIMER.length"
        v-show="isDisclaimerVisible"
        v-html="offer.OFFER_COPY_DISCLAIMER"
      ></div>

      <peppermill-actions>
        <a href="#" @click.prevent="isDisclaimerVisible = !isDisclaimerVisible">
          <template v-if="isDisclaimerVisible">HIDE DISCLAIMER</template>
          <template v-else>SHOW DISCLAIMER</template>
        </a>
        <template
          v-if="
            offer?.OFFER_CAN_BE_REDEEMED === 'YES' &&
            offer?.RESNET_OFFER_CODE.length &&
            offer?.OFFER_ACCEPT_STATUS === 'ACTIVE'
          "
        >
          <a
            v-if="offer.CASINO_PATRON_ID === appStore.state.user?.account_id"
            href="#"
            @click.prevent="validateHotelOffer(offer)"
            >BOOK ROOM</a
          >
          <a
            v-else
            class="layout--action_button-disabled"
            title="Cannot book offers for linked accounts"
            href="#"
            @click.prevent="
              appStore.addSnack({
                duration: 10000,
                message: 'You must login to ' + offer.CASINO_PATRON_FULL_NAME + '\'s account to book this offer.',
                icon: 'warning',
              })
            "
            >BOOK ROOM</a
          >
        </template>
        <a
          v-if="offer?.OFFER_CAN_BE_REDEEMED === 'YES' && !offer?.RESNET_OFFER_CODE.length"
          @click.stop.prevent="appStore.offerManagementDialog.offer = offer"
          href="#"
          >REDEEM</a
        >
        <a
          v-else-if="offer?.OFFER_CAN_BE_VOIDED === 'YES'"
          @click.stop.prevent="appStore.offerManagementDialog.offer = offer"
          href="#"
          >CANCEL</a
        >
        <a v-else-if="offer && isOfferSoldOut(offer)" class="layout--action_button-disabled" @click.prevent href="#"
          >SOLD OUT</a
        >
      </peppermill-actions>

      <span
        v-if="offer && isOfferForLinkedAccount(offer)"
        class="offer--linked_account material-icons"
        :title="'This offer is for ' + offer.CASINO_PATRON_FULL_NAME"
        >attribution</span
      >
    </peppermill-content-block>
  </peppermill-viewport>
</template>

<style scoped>
.view_offer--back_btn {
  display: flex;
  margin-bottom: 0.25em;
  text-decoration: none;
  align-items: center;
}

.view_offer--back_btn_wrapper {
  max-width: 648px;
  margin: 0 auto;
}

.layout--content_block {
  max-width: 600px;
  margin: 0 auto;
}

.offer--image {
  width: 100%;
  height: auto;
}

/*Mirrors .offer--status_row on the card. The margin stands in for the default paragraph margin of the <p> this
  replaced, so the vertical rhythm around the title is unchanged.*/
.offer--status_row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25em 0.5em;
  margin: 1em 0;
}

.offer--linked_account {
  color: rgb(var(--primary-color));
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(30%, -30%);
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 2px 2px 8px 0 rgb(0 0 0 / 35%);
  z-index: 1;
  font-size: 2.5rem;
}

.offer--disclaimer {
  margin-top: 1em;
}
</style>
